import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCarSide, FaChartBar, FaCalendarWeek, FaExchangeAlt, FaInfoCircle, FaAngleDown, FaAngleUp } from 'react-icons/fa';

const VehicleUtilization = ({ rentals, handovers }) => {  // Calculate utilization metrics by individual vehicle instance
  const [expandedVehicleId, setExpandedVehicleId] = useState(null);
  
  const vehicleStats = useMemo(() => {
    const today = new Date();
    const statsMap = {};
    
    // Group rentals by vehicle instance - only include pending or confirmed rentals
    rentals
      .filter(rental => rental.status === 'pending' || rental.status === 'confirmed')
      .forEach(rental => {
        // Use vehicleInstanceId for individual vehicles if available
        const vehicleId = rental.vehicleInstanceId || rental.car?._id || rental.carName;
        
        // Create a more detailed vehicle name including license plate if available
        let vehicleName = rental.carName;
        if (rental.vehicleDetails?.licensePlate) {
          vehicleName = `${rental.carName} (${rental.vehicleDetails.licensePlate})`;
        }
        
        if (!statsMap[vehicleId]) {
          statsMap[vehicleId] = {
            id: vehicleId,
            name: vehicleName,
            make: rental.car?.make || rental.carName?.split(' ')[0] || '',
            model: rental.car?.model || rental.carName?.split(' ').slice(1).join(' ') || '',
            license: rental.vehicleDetails?.licensePlate || '',
            vin: rental.vehicleDetails?.vin || '',
            totalDays: 0,
            totalRentals: 0,
            revenue: 0,
            activeNow: false,
            upcomingRentals: 0,
            handovers: [] // Track handovers for this vehicle
          };
        }
        
        const pickupDate = new Date(rental.pickupDateTime);
        const returnDate = new Date(rental.returnDateTime);
        const daysRented = Math.ceil((returnDate - pickupDate) / (1000 * 60 * 60 * 24));
        
        statsMap[vehicleId].totalDays += daysRented;
        statsMap[vehicleId].totalRentals += 1;
        
        if (rental.totalPrice) {
          statsMap[vehicleId].revenue += rental.totalPrice;
        }
        
        // Check if rental is active now
        if (pickupDate <= today && returnDate >= today) {
          statsMap[vehicleId].activeNow = true;
        }
        
        // Check if rental is upcoming
        if (pickupDate > today) {
          statsMap[vehicleId].upcomingRentals += 1;
        }
      });
    
    // If handovers are provided, associate them with vehicles
    if (handovers) {
      // Flatten the handovers object if it's a map of rental IDs to handover arrays
      const allHandovers = Array.isArray(handovers) 
        ? handovers 
        : Object.values(handovers).flat();
      
      allHandovers.forEach(handover => {
        if (handover.vehicleInstanceId) {
          const vehicleId = handover.vehicleInstanceId;
          if (statsMap[vehicleId]) {
            statsMap[vehicleId].handovers.push(handover);
          }
        }
      });
    }
    
    // Convert to array and sort by total rental days (descending)
    return Object.values(statsMap)
      .sort((a, b) => b.totalDays - a.totalDays)
      .slice(0, 5); // Top 5 most utilized vehicles
  }, [rentals, handovers]);
  
  // Find the maximum values for scaling the bars
  const maxDays = useMemo(() => 
    vehicleStats.length ? Math.max(...vehicleStats.map(v => v.totalDays)) : 0, 
    [vehicleStats]
  );
  
  const toggleExpand = (vehicleId) => {
    setExpandedVehicleId(expandedVehicleId === vehicleId ? null : vehicleId);
  };
  
  return (
    <div className="space-y-4 max-h-[300px] overflow-y-auto pr-1">
      {vehicleStats.length > 0 ? (
        <>
          {vehicleStats.map((vehicle, index) => (
            <motion.div
              key={vehicle.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="p-3 rounded-lg border border-gray-100 hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center mr-2">
                    <FaCarSide className="text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-gray-900 flex items-center">
                      {vehicle.name}
                      {vehicle.activeNow && (
                        <span className="ml-2 text-xxs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          Active Now
                        </span>
                      )}
                    </h4>
                    {vehicle.license && (
                      <p className="text-xs text-gray-600">Plate: {vehicle.license}</p>
                    )}
                    {vehicle.vin && (
                      <p className="text-xxs text-gray-500 truncate">VIN: {vehicle.vin}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center text-xs font-medium">
                  <span className="text-gray-500 mr-2">Utilization:</span>
                  <span className={
                    vehicle.totalDays > 20 ? "text-green-600" : 
                    vehicle.totalDays > 10 ? "text-blue-600" : "text-gray-600"
                  }>
                    {vehicle.totalDays} days
                  </span>
                </div>
              </div>
              
              {/* Utilization bar */}
              <div className="w-full h-2 bg-gray-100 rounded-full mt-2 overflow-hidden">
                <motion.div 
                  className="h-full bg-indigo-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(vehicle.totalDays / (maxDays || 1)) * 100}%` }}
                  transition={{ duration: 0.8, delay: 0.2 + (index * 0.1) }}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs text-gray-600">
                <div className="flex flex-col items-center p-1 rounded-md bg-gray-50">
                  <FaCalendarWeek className="text-indigo-500 mb-1" />
                  <span className="font-medium">{vehicle.totalRentals}</span>
                  <span>Rentals</span>
                </div>
                <div className="flex flex-col items-center p-1 rounded-md bg-gray-50">
                  <FaChartBar className="text-green-500 mb-1" />
                  <span className="font-medium">€{vehicle.revenue.toFixed(0)}</span>
                  <span>Revenue</span>
                </div>
                <div className="flex flex-col items-center p-1 rounded-md bg-gray-50">
                  <FaCarSide className="text-amber-500 mb-1" />
                  <span className="font-medium">{vehicle.upcomingRentals}</span>
                  <span>Upcoming</span>
                </div>
              </div>
              
              {/* Handover information */}
              {vehicle.handovers && vehicle.handovers.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => toggleExpand(vehicle.id)}
                    className="w-full flex items-center justify-between text-xs text-indigo-600 hover:text-indigo-800 py-1"
                  >
                    <span className="flex items-center">
                      <FaExchangeAlt className="mr-1" />
                      {vehicle.handovers.length} Recent Handovers
                    </span>
                    {expandedVehicleId === vehicle.id ? (
                      <FaAngleUp />
                    ) : (
                      <FaAngleDown />
                    )}
                  </button>
                  
                  <AnimatePresence>
                    {expandedVehicleId === vehicle.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-2 space-y-2 text-xxs">
                          {vehicle.handovers.slice(0, 3).map((handover, idx) => (
                            <div 
                              key={`${vehicle.id}-handover-${idx}`}
                              className={`p-2 rounded ${
                                handover.handoverType === 'pickup' 
                                  ? 'bg-teal-50 text-teal-700'
                                  : 'bg-purple-50 text-purple-700'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-medium capitalize">
                                  {handover.handoverType} Handover
                                </span>
                                <span className={`px-1 py-0.5 rounded-full text-xxs ${
                                  handover.status === 'completed' 
                                    ? 'bg-green-100 text-green-700'
                                    : handover.status === 'disputed'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {handover.status}
                                </span>
                              </div>
                              <p className="mt-1">
                                {new Date(handover.handoverDate).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              {handover.mileage && (
                                <p>Mileage: {handover.mileage} km</p>
                              )}
                              {handover.damages && handover.damages.length > 0 && (
                                <p className="text-red-600">
                                  {handover.damages.length} damage{handover.damages.length !== 1 ? 's' : ''} reported
                                </p>
                              )}
                            </div>
                          ))}
                          
                          {vehicle.handovers.length > 3 && (
                            <p className="text-center text-gray-500 mt-1">
                              + {vehicle.handovers.length - 3} more handovers
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          ))}
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <FaChartBar className="mx-auto text-gray-300 mb-2" size={24} />
          <p>No utilization data available</p>
        </div>
      )}
    </div>
  );
};

export default VehicleUtilization;
